import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSettings, useSingleSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { logger } from '@/logging/logger';
import { transactionsLoginIdentity } from 'integration/generated/transactions';
import { RequestParams } from 'integration/generated/transactions/http-client';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import { useCallback, useEffect, useMemo, useState } from 'react';

const recaptchaVerifyTokenFetcher =
	(pub: boolean) =>
	async (storeId: string, token: string, query: Record<string, string>, params: RequestParams) => {
		const response = await transactionsLoginIdentity(pub).recaptchaVerifyToken(
			storeId,
			{ token },
			query,
			params
		);
		return response;
	};

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';

declare global {
	const grecaptcha: {
		ready: (cb: () => void) => void;
		execute: (siteKey: string, options: { action: string }) => Promise<string>;
	};
}

export const useRecaptchaV3 = (siteKey?: string) => {
	const [isLoadedRecaptcha, setIsLoadedRecaptcha] = useState(false);

	useEffect(() => {
		if (!siteKey) return;

		const loadReCaptcha = () => {
			if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
				const script = document.createElement('script');
				script.id = RECAPTCHA_SCRIPT_ID;
				script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
				script.async = true;
				script.onload = () => setIsLoadedRecaptcha(true);
				script.onerror = () => setIsLoadedRecaptcha(false);
				document.body.appendChild(script);
			}
		};

		loadReCaptcha();

		return () => {
			const script = document.getElementById(RECAPTCHA_SCRIPT_ID);

			if (script) {
				script.remove();
			}
		};
	}, [siteKey]);

	const executeRecaptcha = useCallback(
		(action: string): Promise<string | undefined> =>
			new Promise((resolve) => {
				if (!isLoadedRecaptcha || !grecaptcha || !siteKey) {
					return resolve(undefined);
				}

				grecaptcha.ready(() => {
					grecaptcha.execute(siteKey, { action }).then((token: string) => {
						resolve(token);
					});
				});
			}),
		[isLoadedRecaptcha, siteKey]
	);

	return { isLoadedRecaptcha, executeRecaptcha };
};

export const useRecaptchaSettings = () => {
	const { settings, loading } = useSingleSettings();

	const baseSettings = useMemo(() => {
		const userData = settings?.userData || {};
		const google_recaptcha_enabled = getValueByPathFromUnknown(userData, [
			'google_recaptcha_enabled',
		]);
		const google_recaptcha_key_site = getValueByPathFromUnknown(userData, [
			'google_recaptcha_key_site',
		]);

		return {
			recaptchaEnabled: google_recaptcha_enabled === '1',
			recaptchaKeySite:
				typeof google_recaptcha_key_site === 'string' ? google_recaptcha_key_site : undefined,
		};
	}, [settings?.userData]);

	return { ...baseSettings, loading };
};

export const useRecaptchaVerifyToken = () => {
	const params = useExtraRequestParameters();
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId } = getClientSideCommon(settings, router);

	const recaptchaVerifyToken = useCallback(
		async (token: string) => {
			try {
				const response = await recaptchaVerifyTokenFetcher(true)(storeId, token, {}, params);
				return response;
			} catch (error) {
				logger.error(error, 'Error verifying recaptcha token');
				return null;
			}
		},
		[storeId, params]
	);

	return {
		recaptchaVerifyToken,
	};
};

export const useRecaptchaV3Setup = () => {
	const { recaptchaEnabled, recaptchaKeySite, loading: loadingSettings } = useRecaptchaSettings();
	const { isLoadedRecaptcha, executeRecaptcha } = useRecaptchaV3(
		recaptchaEnabled ? recaptchaKeySite : undefined
	);

	return {
		recaptchaEnabled,
		recaptchaExecute: executeRecaptcha,
		recaptchaLoading: loadingSettings || (recaptchaEnabled ? !isLoadedRecaptcha : false),
	};
};
